import { Alert, Box, Link, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AxiosResponse } from "axios";
import http from "../../utils/http";
import {
  a11yProps,
  CustomTabPanel,
  CustomTabPanel2,
  MainTabPanel,
  StyledTab,
  StyledTabs,
} from "../Common/UI/TabPanel";
import ParticipantDetails from "./Tabs/ParticipantDetails";
import ParticipantStudies from "./Tabs/ParticipantStudies";
import ParticipantScreening from "./Tabs/ParticipantScreening";
import {
  HeaderLeftContent,
  HeaderRightContent,
  StyledHeader,
} from "../Common/styles/header";
import { useQuery } from "../../utils/hooks";
import ScreenerHeaderDetails from "./ScreenerHeaderDetails";
import { platformAdminPermissions } from "../../utils/roles";
import { useAppSelector } from "../../Redux/hooks";
import { useNavigate } from "react-router-dom";
import BackButton from "../Common/UI/BackButton";
import GlobalDocuments from "./GlobalDocuments/GlobalDocuments";
import DocumentHeader from "./DocumentHeader";
import HardDeleteEligibleList from "./Tabs/HardDeleteEligibleList";
import { scrollableTabPageContent } from "../../utils/dataTableShell";

const GlobalParticipantTabs = () => {
  const { participantId } = useParams<{ participantId: string }>();
  const [type, setType] = useState<string>("details");
  const participantName = useQuery().get("participant") || "";
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const { platformPermissions } = useAppSelector((state) => state.user);
  const [toggleLoader, setToggleLoader] = useState(false);

  // Single source of truth for the participant. Lifted from ParticipantDetails
  // so every sibling tab/header can derive isLocked from the same fetch
  // instead of re-fetching independently or being unaware of redaction state.
  const [participant, setParticipant] = useState<any>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const refreshParticipant = useCallback(
    () => setRefreshTick((t) => t + 1),
    [],
  );

  useEffect(() => {
    if (!participantId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res: AxiosResponse = await http.get(
          `/admin/registry/participants/${participantId}`,
        );
        if (!cancelled) setParticipant(res?.data?.data ?? null);
      } catch {
        if (!cancelled) setParticipant(null);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [participantId, refreshTick]);

  const redactionStatus: string = participant?.redactionStatus ?? "active";
  const isRedacted = redactionStatus === "redacted";
  const isDeactivated = redactionStatus === "deactivated";
  const isLocked = isRedacted || isDeactivated;

  const mergedInto = participant?.isMerged ? participant?.mergedInto : null;
  const mergedIntoName = mergedInto
    ? [mergedInto.firstName, mergedInto.lastName].filter(Boolean).join(" ") ||
      mergedInto.participantCode ||
      "another participant"
    : "";
  const openGoldenRecord = () => {
    if (!mergedInto?.id) return;
    navigate(
      `/app/participants/${mergedInto.id}?participant=${encodeURIComponent(
        mergedIntoName,
      )}`,
    );
  };

  const [canView] = useState(
    platformPermissions.includes(platformAdminPermissions.managePlatform),
  );

  const [canHardDelete] = useState(
    platformPermissions.includes(platformAdminPermissions.hardDelete),
  );

  // Phase 6c — gate the study-tier Revive action separately from hard-delete.
  const [canStudyRevive] = useState(
    platformPermissions.includes(platformAdminPermissions.studyRevive),
  );

  const handleChange = (_: any, newValue: string) => {
    setType(newValue);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <>
      <StyledHeader>
        <Box sx={HeaderLeftContent}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BackButton onClick={handleBack} />
            <Typography variant="h2" color="text.primary">
              {participantName || ""}
            </Typography>
          </Box>
        </Box>
        <Box sx={HeaderRightContent}>
          {type === "screening" && canView && (
            <ScreenerHeaderDetails type={type} isLocked={isLocked} />
          )}
          {type === "documents" && canView && (
            <DocumentHeader
              setToggleLoader={setToggleLoader}
              isLocked={isLocked}
            />
          )}
        </Box>
      </StyledHeader>

      {participant?.isMerged && (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          This record was merged into{" "}
          {mergedInto?.id ? (
            <Link component="button" onClick={openGoldenRecord}>
              {mergedIntoName}
              {mergedInto.participantCode
                ? ` (${mergedInto.participantCode})`
                : ""}
            </Link>
          ) : (
            "another participant"
          )}
          . It is hidden from registry lists and cohorts; study data recorded
          against it stays here.
        </Alert>
      )}

      {isRedacted && (
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          This participant has been redacted. All actions on this page are
          read-only.
        </Alert>
      )}

      <Box sx={{ borderTop: 1, borderBottom: 1, borderColor: "#E5E7EB" }}>
        <StyledTabs value={type} onChange={handleChange}>
          <StyledTab
            sx={{ display: "flex", gap: "10px", minHeight: "48px" }}
            label="Details"
            value="details"
            {...a11yProps(0)}
          />
          <StyledTab
            sx={{ display: "flex", gap: "10px", minHeight: "48px" }}
            label="Studies"
            value="studies"
            {...a11yProps(1)}
          />
          {canView && (
            <StyledTab
              sx={{ display: "flex", gap: "10px", minHeight: "48px" }}
              label="Screening"
              value="screening"
              {...a11yProps(1)}
            />
          )}
          <StyledTab
            sx={{ display: "flex", gap: "10px", minHeight: "48px" }}
            label="Documents"
            value="documents"
            {...a11yProps(2)}
          />
          {canHardDelete && (
            <StyledTab
              sx={{ display: "flex", gap: "10px", minHeight: "48px" }}
              label="Deletion"
              value="deletion"
              {...a11yProps(4)}
            />
          )}
        </StyledTabs>
      </Box>
      <Box sx={scrollableTabPageContent("calc(100% - 122px)", 0)}>
        <MainTabPanel value={type} index={"details"}>
          <ParticipantDetails
            participant={participant}
            isLocked={isLocked}
            refreshParticipant={refreshParticipant}
          />
        </MainTabPanel>
        <CustomTabPanel2 value={type} index={"studies"}>
          <Box sx={{ p: 2.5, height: "100%", minHeight: 0 }}>
            <ParticipantStudies
              isLocked={isLocked}
              canStudyRevive={canStudyRevive}
            />
          </Box>
        </CustomTabPanel2>
        {canView && (
          <CustomTabPanel value={type} index={"screening"}>
            <ParticipantScreening
              showModal={showModal}
              setShowModal={setShowModal}
              isLocked={isLocked}
            />
          </CustomTabPanel>
        )}
        <MainTabPanel value={type} index={"documents"}>
          <GlobalDocuments
            toggleLoader={toggleLoader}
            setToggleLoader={setToggleLoader}
            isLocked={isLocked}
          />
        </MainTabPanel>
        {canHardDelete && (
          <CustomTabPanel2 value={type} index={"deletion"}>
            <Box sx={{ p: 2.5, height: "100%", minHeight: 0 }}>
              <HardDeleteEligibleList />
            </Box>
          </CustomTabPanel2>
        )}
      </Box>
    </>
  );
};

export default GlobalParticipantTabs;
